<template>
  <div class="partner-business-wrapper">
    <TopProgressBar v-if="isMobile" :isBackShown="!isMultiBusinessFlow" prevStep="partner-personal-details"/>
    <div class="main-layout partner-business-view" :class="viewClass">
      <h1 class="page-title">{{isMultiBusinessFlow? 'Lets get started' : 'Your business details' }}</h1>
      <h1 class="multi-business-subtitle" v-if="isMultiBusinessFlow">You're about to create a new business account under your existing Lili profile, using your current personal details.</h1>
      <v-form
        class="-helper--flex column fluid"
        id="v-form"
        ref="form"
        @submit.prevent=""
      >

      {{ /** Section Business Type */ }}
      <div class="business-data-section" ref="businessTypeSection">
        <h1 class="section-title">Business type</h1>
        <BusinessTypeForm/>
      </div>
      {{ /** Section Business Details */ }}
      <div v-if="customerData.incomeType.enum" ref="businessDetailsSection" class="business-data-section relative">
        <h1 class="section-title">Business details</h1>
        <h1 class="section-sub-title">Please select the industry that best describes your business</h1>
        <BusinessDetailsForm />
      </div>
      {{ /** Section Business Address */ }}
      <div v-if="customerData.incomeType.enum && !shouldHideAddressSection" ref="businessAddressSection" class="business-data-section relative">
        <h1 class="section-title">Business address</h1>
        <h1 class="section-sub-title">Please provide your primary business address (not your registered agent address).</h1>
        <BusinessAddressForm />
      </div>
      {{ /** Section Business Owners */ }}
      <div v-if="customerData.incomeType.enum && !shouldHideOwnersSection" ref="businessOwnersSection" class="business-data-section relative">
        <h1 class="section-title">Business owners</h1>
        <h1 class="section-sub-title">Please specify your ownership in the business, and provide all ultimate beneficial owners (UBOs) that hold 25% or more of the business.</h1>
        <BusinessOwners />
      </div>
      {{ /** Footer */ }}
      <ActionFooter :errMsg="localError.ownerPercent" :is-loading="flags.isLoading" @onsubmit="handleSubmit"  />
      </v-form>
      <MobileFooter v-if="isMobile" class="full" />
    </div>
  </div>
</template>

<script>
import BusinessTypeForm from './business-sections/BusinessTypeForm.vue'
import BusinessDetailsForm from './business-sections/BusinessDetailsForm.vue'
import BusinessAddressForm from './business-sections/BusinessAddressForm.vue'
import BusinessOwners from './business-sections/BusinessOwners.vue'
import ActionFooter from './business-sections/ActionFooter.vue'
import {params} from '../../localData'
import {updateCustomerStep} from '../../services/UtilService'
import {findAndScrollToFormError, scrollToServerErrorBySection} from '../../services/entities/errorService'
import TopProgressBar from '../../components/globalUiComponents/TopProgressController.vue'
import MobileFooter from '../../components/globalUiComponents/MobileFooter.vue'

export default {
  name: 'PartnerBusiness',
  components: {
    BusinessTypeForm,
    BusinessDetailsForm,
    BusinessAddressForm,
    BusinessOwners,
    ActionFooter,
    MobileFooter,
    TopProgressBar
  },
  async mounted () {
    updateCustomerStep(this.$store, 2, this.customerData.accessToken, this.customerData.customerId)
  },
  data () {
    return {
      flags: {
        isLoading: false
      },
      localError: {
        ownerPercent: ''
      }
    }
  },
  computed: {
    shouldHideAddressSection () {
      return this.customerData.incomeType.enum === 'SSN' || this.customerData.incomeType.enum === 'DBA'
    },
    shouldHideOwnersSection () {
      return this.customerData.incomeType.enum === 'LLC' || this.shouldHideAddressSection
    },
    customerData () {
      return this.$store.getters['customer/data']
    },
    isMobile () {
      return this.$store.getters['onboarding/isMobile']
    },
    ajaxDataBusinessDetails () {
      const shouldNotSendEIN = this.customerData.incomeType.enum === 'SSN' || this.customerData.incomeType.enum === 'DBA'
      return {
        method: 'post',
        endPoint:
          params.restEndpoints.address + this.customerData.customerId + '/business',
        data: {
          segment: this.customerData.segment,
          naicsIndustry: this.customerData.sub_industry,
          mainIncomeSource: this.customerData.incomeType.enum || null,
          businessName: this.customerData.businessName || null,
          incorporationState: this.customerData.businessRegisteredState || null,
          ...(!shouldNotSendEIN) && {ein: (this.customerData.ein && this.customerData.ein.replace('-', '')) || null}
        },
        self: this
      }
    },
    ajaxDataBusinessAddress () {
      return {
        method: 'post',
        endPoint:
          params.restEndpoints.address + this.customerData.customerId + '/setBusinessAddress',
        data: {
          line1: this.customerData.businessLine1,
          line2: this.customerData.businessLine2,
          city: this.customerData.businessCity,
          postalCode: this.customerData.businessPostalCode,
          state: this.customerData.businessAddressState,
          sameAsPrimary: this.customerData.isPersonalAndBusinessAddressSame || false
        },
        self: this
      }
    },
    ownerShipAjaxData () {
      return {
        method: 'post',
        endPoint: `v3/secureonboarding/partner/user/${this.customerData.customerId}/business/${this.customerData.businessId}/ownershipPercent`,
        data: {
          ownershipPercent: this.customerData.ownershipPercent
        },
        self: this
      }
    },
    isMultiBusinessFlow () {
      return this.$store.getters['onboarding/isMultiBusinessFlow']
    },
    viewClass (){
      return {
        'multi-business': this.isMultiBusinessFlow,
        'partner-business-view-full-mobile': this.isMobile && !this.customerData.businessType
      }
    }
  },
  methods: {
    async initOwnershipPercent () {
      try {
        await this.$store.dispatch(
          'customer/updateCustomer',
          {
            ...this.ownerShipAjaxData,
            header: {
              headerName: params.httpHeaders.auth,
              headerValue: 'lili ' + this.customerData.accessToken
            }
          },
          this
        )
      } catch (e) {
        this.localError.ownerPercent = e.content
        throw e
      }
    },
    async handleBusinessDetails () {
      try {
        const res = await this.$store.dispatch(
          'customer/updateCustomer',
          {
            ...this.ajaxDataBusinessDetails,
            header: {
              headerName: params.httpHeaders.auth,
              headerValue: 'lili ' + this.customerData.accessToken
            }
          },
          this
        )
        this.$store.commit('customer/setKeyValue', {prop: 'businessId', value: res.externalId})
        if ('primaryOwnershipPercent' in res) {
          this.$store.commit('customer/setKeyValue', {prop: 'ownershipPercent', value: res.primaryOwnershipPercent})
        }
        window.hj('identify', {businessType: this.customerData.segment})
      } catch (e) {
        throw e
      }
    },
    async handleBusinessAddress () {
      try {
        await this.$store.dispatch(
          'customer/updateCustomer',
          {
            ...this.ajaxDataBusinessAddress,
            header: {
              headerName: params.httpHeaders.auth,
              headerValue: 'lili ' + this.customerData.accessToken
            }
          },
          this
        )
      } catch (e) {
        throw e
      }
    },
    async handleSubmit () {
      if (!this.$refs.form.validate()) {
        findAndScrollToFormError(this.$refs.form)
        return
      }
      this.localError.ownerPercent = ''
      if (this.customerData.ownershipPercent === '') {
        this.$store.commit('customer/setKeyValue', {prop: 'ownershipPercent', value: 0})
      }
      try {
        this.flags.isLoading = true
        await this.handleBusinessDetails()
        if (!this.shouldHideOwnersSection) {
          await this.initOwnershipPercent()
        }
        if (!this.shouldHideAddressSection) {
          await this.handleBusinessAddress()
        }
        this.flags.isLoading = false
        this.$router.push({name: 'plan'})
      } catch (e) {
        this.flags.isLoading = false
        scrollToServerErrorBySection(
          this.$refs,
          {
            businessDetailsSection: ['name', 'ein'],
            businessAddressSection: ['line1', 'line2', 'city', 'zipcode']
          },
          e.field
        )
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/PartnerOnboarding/PartnerBusiness.vue